import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LayoutGrid, Minus, Plus, PlusCircle } from 'lucide-react';
import type { DeviceDto } from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { ReservationAlertsBanner } from '@/features/bookings/ReservationAlertsBanner';
import { StartOverBookingConfirm } from '@/features/bookings/StartOverBookingConfirm';
import { shouldConfirmWalkIn } from '@/features/bookings/bookingLogic';
import { useReservationAlerts } from '@/features/bookings/useBookings';
import { AddDeviceDialog } from './AddDeviceDialog';
import { FilterDisclosure } from '@/components/layout/FilterDisclosure';

import { AssistBanner } from './AssistBanner';
import { BulkConfirmDialog } from './BulkConfirmDialog';
import { BulkPauseDialog } from './BulkPauseDialog';
import { DeleteDeviceDialog } from './DeleteDeviceDialog';
import { DeviceTile, type TileAction } from './DeviceTile';
import { EndReservationDialog } from './EndReservationDialog';
import { HoldClockDialog } from './HoldClockDialog';
import { VoidSessionDialog } from './VoidSessionDialog';
import { OutstandingBillsDialog } from './OutstandingBillsDialog';
import { useLayoutEditor } from './useLayoutEditor';
import { PairDeviceDialog } from './PairDeviceDialog';
import { PlayAdDialog } from './PlayAdDialog';
import { PortalAppVersion } from './PortalAppVersion';
import { ReservationDrawer } from './ReservationDrawer';
import { SelectionBar, type BulkActionId } from './SelectionBar';
import { ServiceStateDialog } from './ServiceStateDialog';
import { bulkPlay, bulkPower, bulkSupportUnlock, type BulkOutcome } from './bulkActions';
import { bulkNeedsTypedConfirm } from './deviceCapabilities';
import { markLauncherNoticeDone } from './launcherNotices';
import { useAssist } from './useAssist';
import type { DeviceMenuItemId } from './deviceMenu';
import { EMPTY_FILTERS, useDeviceCategories, useDevices, type DeviceFilters } from './useDevices';

/**
 * Card width in rem at 100%. The zoom control scales this.
 *
 * 16rem ≈ the 258px tile of the approved board card (its 1180px viewport at
 * four columns), so 100% zoom shows the tile at the proportions the owner
 * signed off. It is also sized for the product shot rather than the text: at
 * 10rem the machine was roughly a 96px square and staff could not tell a PS5
 * from a PC at a glance, which is the one job this board has.
 */
const BASE_TILE_REM = 16;
const ZOOM_STEPS = [50, 75, 100, 125, 150] as const;

/**
 * The selection with every id that is no longer on the board removed (R4#8).
 *
 * Filters must narrow the SELECTION as well as the grid: bulk actions run
 * over the selected devices, and an id a filter hid would let "Shutdown 3"
 * reach machines nobody can currently see. Returns the SAME set when nothing
 * was pruned, so the effect that applies it on every poll re-renders nothing.
 * Exported for the node-only pin.
 */
export function pruneSelection(
  selected: ReadonlySet<string>,
  visibleIds: readonly string[],
): ReadonlySet<string> {
  const visible = new Set(visibleIds);
  const kept = [...selected].filter((id) => visible.has(id));
  return kept.length === selected.size ? selected : new Set(kept);
}

/**
 * A bulk action pending its confirm. Every action goes through the same dialog
 * — it is what reports each machine's own answer — but only the destructive
 * ones make the employee type the count; see `BulkConfirmDialog.typedConfirm`.
 */
type PendingBulk = BulkActionId;

/**
 * The Device List board (SPEC §5.2) — the screen a shift is run from.
 *
 * State filtering is client-side so the chips can always show counts across the
 * whole venue rather than counts of whatever filter is active.
 */
export function DeviceListPage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const navigate = useNavigate();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const [filters, setFilters] = useState<DeviceFilters>(EMPTY_FILTERS);
  const [adding, setAdding] = useState(false);
  // Tracked as an index into ZOOM_STEPS rather than a raw percentage, so the
  // buttons can never land on a value that is not a step.
  const [zoomStep, setZoomStep] = useState(ZOOM_STEPS.indexOf(100));
  const zoom = ZOOM_STEPS[zoomStep] ?? 100;
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [openDevice, setOpenDevice] = useState<DeviceDto | null>(null);
  /** Walk-in start pending the soft confirm over an upcoming booking. */
  const [confirmDevice, setConfirmDevice] = useState<DeviceDto | null>(null);
  /** Banner rows staff waved away, per booking id — not per device. */
  const [dismissedBookingIds, setDismissedBookingIds] = useState<ReadonlySet<string>>(new Set());

  // Actions-menu dialogs (wave 3 #5), one open at a time.
  const [editingDevice, setEditingDevice] = useState<DeviceDto | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<DeviceDto | null>(null);
  const [billsDevice, setBillsDevice] = useState<DeviceDto | null>(null);
  const [endingDevice, setEndingDevice] = useState<DeviceDto | null>(null);
  // The BILLING clock, not the board's Play/Pause pair — see HoldClockDialog.
  const [holdingDevice, setHoldingDevice] = useState<DeviceDto | null>(null);
  const [voidingDevice, setVoidingDevice] = useState<DeviceDto | null>(null);
  const [pairingDevice, setPairingDevice] = useState<DeviceDto | null>(null);
  const [serviceDevice, setServiceDevice] = useState<DeviceDto | null>(null);

  // Bulk actions over the checkbox selection (wave 3 #12, #6-UI, #11).
  const [pendingBulk, setPendingBulk] = useState<PendingBulk | null>(null);
  /** Devices whose sitting is being opened together, on one shared tab. */
  const [bulkReserve, setBulkReserve] = useState<readonly DeviceDto[] | null>(null);
  const [playAdOpen, setPlayAdOpen] = useState(false);
  /** Why the last Done on a tile's launcher notice did not land (2026-10-05). */
  const [noticeError, setNoticeError] = useState<string | null>(null);

  // Gates ribbon/banner/confirm only; nextBooking DATA always arrives.
  const reservationAlertsEnabled = useReservationAlerts();

  // "Summon a human" (wave 3 #13): its own 10s poll beside the device poll.
  const assist = useAssist();

  const { categories } = useDeviceCategories();
  /*
   * The server is asked for the WHOLE board every time (hidden aside); every
   * filter — category, state, search — is applied client-side over that one
   * result. This is what lets the chips row carry venue-wide counts whatever
   * is filtered, and makes switching a chip instant instead of a round-trip.
   * A venue is ~80 rows; filtering them in memory costs nothing.
   */
  const { data, receivedAt, loading, error, reload } = useDevices({
    ...EMPTY_FILTERS,
    showHidden: filters.showHidden,
  });

  // Elapsed time ticks without re-fetching, so the clock is its own state.
  // Every second: the approved tile shows a live H:MM:SS second hand, and a
  // seconds display driven by a 30s tick reads as a frozen (broken) timer.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);

  // Keep the open drawer bound to fresh data, so its running cost updates.
  useEffect(() => {
    if (!openDevice || !data) return;
    const fresh = data.devices.find((device) => device.id === openDevice.id);
    if (fresh) setOpenDevice(fresh);
  }, [data, openDevice?.id]);

  const visible = useMemo(() => {
    if (!data) return [];
    const query = filters.search.trim().toLowerCase();
    return data.devices.filter(
      (device) =>
        (!filters.status || device.status === filters.status) &&
        (!filters.categoryId || device.categoryId === filters.categoryId) &&
        (!query ||
          device.name.toLowerCase().includes(query) ||
          (device.tag ?? '').toLowerCase().includes(query)),
    );
  }, [data, filters.status, filters.categoryId, filters.search]);

  /** Venue-wide tally per category for the chips — always over the FULL board. */
  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const device of data?.devices ?? []) {
      counts.set(device.categoryId, (counts.get(device.categoryId) ?? 0) + 1);
    }
    return counts;
  }, [data]);

  // Changing a filter (or a device vanishing from the data) drops it from the
  // selection too — see pruneSelection. Identity-preserving, so the 10s poll
  // costs no extra renders while nothing changed.
  useEffect(() => {
    setSelectedIds((current) =>
      pruneSelection(
        current,
        visible.map((device) => device.id),
      ),
    );
  }, [visible]);

  /**
   * The selected devices as rows, in board order — derived from VISIBLE, not
   * from the raw data, so a bulk action can never reach an off-screen machine
   * even in the render before the prune effect lands, and the SelectionBar's
   * "N selected" vs "Select all (M)" compare like-for-like sets.
   */
  const selectedDevices = useMemo(
    () => visible.filter((device) => selectedIds.has(device.id)),
    [visible, selectedIds],
  );

  /**
   * Edit layout arranges the WHOLE board, never the filtered view: saving an
   * order derived from a filtered subset would interleave arranged tiles with
   * untouched ones and land somewhere nobody asked for.
   */
  const layout = useLayoutEditor(() => void reload());
  const board = layout.draft ?? visible;

  /*
   * Arranging is refused while anything is filtered out.
   *
   * Saving assigns sortOrder 0..n-1 to the ids sent, and `data.devices` is the
   * SERVER-FILTERED list — so searching "billiard", nudging one tile and saving
   * renumbered four tables to 0-3 and threw them in front of all 36 PCs. The
   * venue's whole arrangement, gone, with no way back but re-dragging it.
   *
   * The honest fix is the button, not the payload: you cannot arrange a board you
   * can only see part of.
   */
  const boardIsFiltered =
    filters.search.trim() !== '' ||
    filters.categoryId !== null ||
    filters.deviceType !== null ||
    filters.status !== null ||
    // Rearranging the board while hidden devices are showing would save an
    // order that the board nobody-sees-them-on does not have.
    filters.showHidden;

  /** deviceId -> its open/acked assist request status, for the tile badges. */
  const assistByDevice = useMemo(
    () => new Map(assist.requests.map((request) => [request.deviceId, request.status])),
    [assist.requests],
  );

  const pillClass =
    'rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover';

  // §7 "Play Ad" targets every FREE TV seat server-side — no selection
  // involved (the old selection-scoped push cached files on PCs that nothing
  // ever played).
  const playAdEnabled = can('media.manage');

  /*
   * The card's filter row: state lives in CHIPS with venue-wide counts, never
   * in a dropdown — the counts double as the board summary staff glance for
   * ("Busy · 14" answers the question before it is asked). A chip presses
   * again to clear itself.
   *
   * ONE definition, rendered twice: as its own row on desktop, and INSIDE the
   * header's Filters disclosure on a phone — ten chips stacked four rows deep
   * over the board was the whole screen (owner, on mobile: "i see [the chips]
   * at all times and it's not in the filters tab").
   */
  const boardFilterControls = (
    <>
      <FilterChip
        on={filters.categoryId === null && filters.status === null}
        label={`${t('deviceBoard.chips.all')} · ${data?.devices.length ?? 0}`}
        onClick={() => setFilters((current) => ({ ...current, categoryId: null, status: null }))}
      />
      {categories.map((category) => (
        <FilterChip
          key={category.id}
          on={filters.categoryId === category.id}
          label={`${category.name} · ${categoryCounts.get(category.id) ?? 0}`}
          onClick={() =>
            setFilters((current) => ({
              ...current,
              categoryId: current.categoryId === category.id ? null : category.id,
            }))
          }
        />
      ))}
      <FilterChip
        on={filters.status === 'BUSY'}
        label={`${t('devices.status.busy')} · ${data?.counts.BUSY ?? 0}`}
        onClick={() =>
          setFilters((current) => ({
            ...current,
            status: current.status === 'BUSY' ? null : 'BUSY',
          }))
        }
      />
      <FilterChip
        on={filters.status === 'ABOUT_TO_END'}
        label={`${t('devices.status.aboutToEnd')} · ${data?.counts.ABOUT_TO_END ?? 0}`}
        onClick={() =>
          setFilters((current) => ({
            ...current,
            status: current.status === 'ABOUT_TO_END' ? null : 'ABOUT_TO_END',
          }))
        }
      />

      {/* Search rides the chip row as its own pill, per the card. Filtering
          is client-side, so it works per keystroke with no debounce. */}
      <input
        value={filters.search}
        onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
        placeholder={t('devices.searchPlaceholder')}
        className="w-44 rounded-full border border-subtle bg-input px-3.5 py-[7px] text-xs text-content outline-none placeholder:text-content-muted focus:border-primary"
      />

      <div className="flex-1" />

      <label className="flex items-center gap-2 text-xs text-content-secondary">
        <input
          type="checkbox"
          checked={filters.showHidden}
          onChange={(event) =>
            setFilters((current) => ({ ...current, showHidden: event.target.checked }))
          }
          className="h-3.5 w-3.5 accent-[var(--brand-primary)]"
        />
        {t('devices.showHidden')}
      </label>

      {/* Edit layout: arrange the tiles to match where the machines are. */}
      {can('devices.manage') ? (
        layout.editing ? (
          <>
            <button
              type="button"
              data-layout-save
              disabled={layout.saving}
              onClick={() => void layout.save()}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-fg hover:bg-primary-hover disabled:opacity-60"
            >
              {layout.saving ? t('common.loading') : t('deviceBoard.layout.save')}
            </button>
            <button type="button" onClick={layout.cancel} disabled={layout.saving} className={pillClass}>
              {t('common.cancel')}
            </button>
            <span className="text-xs text-content-muted">{t('deviceBoard.layout.hint')}</span>
          </>
        ) : (
          <button
            type="button"
            data-layout-start
            disabled={boardIsFiltered}
            title={boardIsFiltered ? t('deviceBoard.layout.clearFiltersFirst') : undefined}
            onClick={() => layout.start(data?.devices ?? [])}
            className={`${pillClass} inline-flex items-center gap-1.5 ${
              boardIsFiltered ? 'cursor-not-allowed opacity-40' : ''
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
            {t('deviceBoard.layout.edit')}
          </button>
        )
      ) : null}
    </>
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('devices.title')}</h1>

        {/* Zoom, so a venue with 40 machines can fit them all on one screen. */}
        <div className="flex items-center gap-1 rounded-full border border-subtle px-2 py-1">
          <ZoomButton
            label={t('devices.zoomIn')}
            onClick={() => setZoomStep((step) => Math.min(step + 1, ZOOM_STEPS.length - 1))}
          >
            <PlusCircle className="h-4 w-4" aria-hidden />
          </ZoomButton>
          <span className="tabular w-11 text-center text-xs text-content-secondary">{zoom}%</span>
          <ZoomButton
            label={t('devices.zoomOut')}
            onClick={() => setZoomStep((step) => Math.max(step - 1, 0))}
          >
            <Minus className="h-4 w-4" aria-hidden />
          </ZoomButton>
        </div>

        <div className="flex-1" />

        <FilterDisclosure
          badge={
            (filters.categoryId ? 1 : 0) +
            (filters.status ? 1 : 0) +
            (filters.search.trim() ? 1 : 0) +
            (filters.showHidden ? 1 : 0)
          }
        >

        {/* Phone only: the chips row lives behind the Filters button, like
            every other toolbar's secondary controls — ten always-visible
            chips were most of a phone screen. Desktop renders its own row
            below the header instead. `roomy`/`compact`, not `md`: a phone
            turned sideways is a phone too (styles/responsive.css). */}
        <div className="flex flex-wrap items-center gap-2 roomy:hidden">{boardFilterControls}</div>

        {/* Has the published kiosk package actually reached the PCs? Nothing
            renders until a package is published and a machine is behind. */}
        <PortalAppVersion devices={data?.devices ?? []} />

        {can('devices.manage') ? (
          <button type="button" onClick={() => setAdding(true)} className={pillClass}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-4 w-4" aria-hidden />
              {t('devices.device')}
            </span>
          </button>
        ) : null}

        {/* §7: a library video takes over every free TV seat, one press. */}
        <button
          type="button"
          disabled={!playAdEnabled}
          onClick={() => setPlayAdOpen(true)}
          title={playAdEnabled ? undefined : t('deviceBoard.playAd.noPermission')}
          className={`${pillClass} ${playAdEnabled ? '' : 'cursor-not-allowed opacity-40'}`}
        >
          {t('devices.playAd')}
        </button>
        {/* The venue-wide view, which this button promised and could not keep
            for two milestones. The per-device cut still lives in the tile's
            Actions menu, for "what does PC-7 owe" without leaving the board. */}
        {can('bills.view') ? (
          <button
            type="button"
            data-outstanding-link
            onClick={() => navigate('/outstanding')}
            className={pillClass}
          >
            {t('devices.outstandingBills')}
          </button>
        ) : null}

        </FilterDisclosure>
      </header>

      {/* Desktop: the chips as their own full-width row under the header.
          On a phone this row hides — the same controls live in the header's
          Filters disclosure above. */}
      <div className="flex flex-wrap items-center gap-2 px-4 compact:hidden">
        {boardFilterControls}
      </div>

      {layout.error ? (
        <p role="alert" className="px-4 text-sm text-danger">
          {layout.error}
        </p>
      ) : null}

      {noticeError ? (
        <p role="alert" data-launcher-notice-error="" className="px-4 text-sm text-danger">
          {noticeError}
        </p>
      ) : null}

      <AssistBanner
        requests={assist.requests}
        now={now}
        muted={assist.muted}
        onToggleMuted={assist.toggleMuted}
        onAck={(id) => void assist.ack(id)}
        onResolve={(id) => void assist.resolve(id)}
        actionError={assist.actionError}
      />

      {reservationAlertsEnabled ? (
        <ReservationAlertsBanner
          devices={data?.devices ?? []}
          now={now}
          dismissedBookingIds={dismissedBookingIds}
          onDismiss={(bookingId) =>
            setDismissedBookingIds((current) => new Set(current).add(bookingId))
          }
        />
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : board.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          <div
            // Uniform 16px gutters per the measured card (board pad 14px); the
            // old outsized y-gap existed for artwork that overflowed above
            // each card and now lives inside the tile.
            className="grid gap-4 pt-3.5"
            style={
              {
                gridTemplateColumns: `repeat(auto-fill, minmax(${(BASE_TILE_REM * zoom) / 100}rem, 1fr))`,
                // Read by DeviceTile for the product shot's height, so zooming
                // scales the picture and not just the card around it.
                '--tile-scale': zoom / 100,
              } as CSSProperties
            }
          >
            {board.map((device, index) => (
              <div
                key={device.id}
                data-layout-tile={layout.editing ? device.id : undefined}
                draggable={layout.editing}
                onDragStart={() => layout.onDragStart(device.id)}
                onDragEnd={layout.onDragEnd}
                onDragOver={(event) => {
                  if (!layout.editing) return;
                  event.preventDefault();
                  layout.onDragOver(device.id);
                }}
                className={
                  layout.editing
                    ? `relative ${layout.dragging === device.id ? 'opacity-40' : ''}`
                    : undefined
                }
              >
                <DeviceTile
                  device={device}
                  now={now}
                  receivedAt={receivedAt}
                  currencySymbol={currencySymbol}
                  selected={selectedIds.has(device.id)}
                  reservationAlertsEnabled={reservationAlertsEnabled}
                  assistStatus={assistByDevice.get(device.id) ?? null}
                  can={can}
                  onToggleSelect={handleToggleSelect}
                  onAction={handleAction}
                  onMenuAction={handleMenuAction}
                  onLauncherNoticeDone={(target, auditId) =>
                    void handleLauncherNoticeDone(target, auditId)
                  }
                />

                {/*
                  While arranging, a shield over the whole card swallows the
                  tile's own controls — dragging a machine must never be one
                  mis-aimed pixel away from starting a session on it. The two
                  nudge buttons are the keyboard and touch path; a board that
                  can only be arranged by dragging cannot be arranged at all on
                  a touchscreen till.
                */}
                {layout.editing ? (
                  <div className="absolute inset-0 z-20 grid cursor-grab place-items-center rounded-card bg-primary/5 ring-2 ring-primary/50 active:cursor-grabbing">
                    <div className="flex items-center gap-1 rounded-full bg-overlay/90 px-1 py-1 shadow-lg">
                      <button
                        type="button"
                        onClick={() => layout.nudge(device.id, -1)}
                        disabled={index === 0}
                        aria-label={t('deviceBoard.layout.moveEarlier', { device: device.name })}
                        className="grid h-7 w-7 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-content disabled:opacity-30"
                      >
                        <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                      </button>
                      <span className="tabular px-1 text-xs font-semibold text-content">
                        {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => layout.nudge(device.id, 1)}
                        disabled={index === board.length - 1}
                        aria-label={t('deviceBoard.layout.moveLater', { device: device.name })}
                        className="grid h-7 w-7 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-content disabled:opacity-30"
                      >
                        <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bulk actions stay out of the way while the board is being arranged. */}
      <SelectionBar
        count={layout.editing ? 0 : selectedDevices.length}
        visibleCount={visible.length}
        selectedTypes={selectedDevices.map((device) => device.deviceType)}
        can={can}
        onClear={() => setSelectedIds(new Set())}
        onSelectAllVisible={() => setSelectedIds(new Set(visible.map((device) => device.id)))}
        onAction={handleBulkAction}
      />

      {adding ? (
        <AddDeviceDialog
          categories={categories}
          onClose={() => setAdding(false)}
          onCreated={() => void reload()}
        />
      ) : null}

      {editingDevice ? (
        <AddDeviceDialog
          categories={categories}
          device={editingDevice}
          onClose={() => setEditingDevice(null)}
          onCreated={() => void reload()}
        />
      ) : null}

      {deletingDevice ? (
        <DeleteDeviceDialog
          device={deletingDevice}
          onClose={() => setDeletingDevice(null)}
          onDeleted={() => void reload()}
        />
      ) : null}

      {billsDevice ? (
        <OutstandingBillsDialog
          device={billsDevice}
          currencySymbol={currencySymbol}
          onClose={() => setBillsDevice(null)}
        />
      ) : null}

      {endingDevice ? (
        <EndReservationDialog
          device={endingDevice}
          onClose={() => setEndingDevice(null)}
          onDone={() => void reload()}
        />
      ) : null}

      {holdingDevice ? (
        <HoldClockDialog
          device={holdingDevice}
          onClose={() => setHoldingDevice(null)}
          onDone={() => void reload()}
        />
      ) : null}

      {voidingDevice?.currentSession ? (
        <VoidSessionDialog
          target={{
            sessionId: voidingDevice.currentSession.id,
            deviceName: voidingDevice.name,
            partyName: voidingDevice.currentSession.tabName,
            state: 'running',
            amountMd: voidingDevice.currentSession.runningCostMd,
          }}
          currencySymbol={currencySymbol}
          onClose={() => setVoidingDevice(null)}
          onDone={() => void reload()}
        />
      ) : null}

      {pairingDevice ? (
        <PairDeviceDialog device={pairingDevice} onClose={() => setPairingDevice(null)} />
      ) : null}

      {serviceDevice ? (
        <ServiceStateDialog
          device={serviceDevice}
          onClose={() => setServiceDevice(null)}
          onSaved={() => void reload()}
        />
      ) : null}

      {pendingBulk === 'pause' ? (
        // Pause ends sittings, so it asks a round-priced one's rounds (OW19 review).
        <BulkPauseDialog
          devices={selectedDevices}
          onClose={() => setPendingBulk(null)}
          onFinished={() => void reload()}
        />
      ) : pendingBulk ? (
        <BulkConfirmDialog
          title={t(`deviceBoard.bulkDialog.${pendingBulk}Title`)}
          message={
            // The support-mode relock warning rides the confirm (wave-3
            // review R2#3): the countdown can fire over a customer seated
            // after the unlock, so staff must hear it BEFORE arming it.
            pendingBulk === 'supportUnlock'
              ? `${t(`deviceBoard.bulkDialog.${pendingBulk}Message`, {
                  count: selectedDevices.length,
                })} ${t('deviceBoard.bulkDialog.supportUnlockRelockWarning')}`
              : t(`deviceBoard.bulkDialog.${pendingBulk}Message`, {
                  count: selectedDevices.length,
                })
          }
          actionLabel={t(`deviceBoard.bulk.${pendingBulk}`)}
          deviceNames={selectedDevices.map((device) => device.name)}
          // Starting play takes nothing away from anyone; the rest do.
          typedConfirm={bulkNeedsTypedConfirm(pendingBulk)}
          run={() => runBulk(pendingBulk)}
          onClose={() => setPendingBulk(null)}
          onFinished={() => void reload()}
        />
      ) : null}

      {playAdOpen ? <PlayAdDialog onClose={() => setPlayAdOpen(false)} /> : null}

      {openDevice ? (
        <ReservationDrawer
          device={openDevice}
          now={now}
          currencySymbol={currencySymbol}
          onClose={() => setOpenDevice(null)}
          onSaved={() => void reload()}
        />
      ) : null}

      {bulkReserve && bulkReserve.length > 0 ? (
        <ReservationDrawer
          device={bulkReserve[0]!}
          party={bulkReserve}
          now={now}
          currencySymbol={currencySymbol}
          onClose={() => setBulkReserve(null)}
          onSaved={() => void reload()}
        />
      ) : null}

      {confirmDevice ? (
        <StartOverBookingConfirm
          device={confirmDevice}
          onConfirm={() => {
            setConfirmDevice(null);
            setOpenDevice(confirmDevice);
          }}
          onClose={() => setConfirmDevice(null)}
        />
      ) : null}
    </div>
  );

  function handleToggleSelect(device: DeviceDto): void {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(device.id)) next.delete(device.id);
      else next.add(device.id);
      return next;
    });
  }

  function handleAction(device: DeviceDto, action: TileAction): void {
    // Out-of-service devices have nothing to reserve or settle; bringing one
    // back is a management action from the tile's own Actions menu.
    if (device.status === 'OUT_OF_SERVICE') return;

    // Wave 3 #4: "Add order" jumps to the cafe with this device (and its open
    // tab, when one is running) in the URL for the order pad to preselect.
    if (action === 'orders') {
      const params = new URLSearchParams({ device: device.id });
      if (device.currentSession?.tabId) params.set('tab', device.currentSession.tabId);
      navigate(`/cafe?${params.toString()}`);
      return;
    }

    // Free the machine. Same dialog as the menu's "End reservation" — one
    // operation, reached from wherever staff happen to be looking: the clock
    // stops, the time played lands on the tab, and the PC returns to its
    // sign-in screen by itself. Money is collected later, at the till.
    if (action === 'stopAndClose') {
      setEndingDevice(device);
      return;
    }

    // Just looking at a machine someone is on. No booking confirm, because
    // nothing is being started.
    if (action === 'details') {
      setOpenDevice(device);
      return;
    }

    if (action === 'reserve') {
      // Soft confirm before starting a walk-in over an upcoming booking —
      // gated, like the ribbon and banner, on enableReservationAlerts.
      if (reservationAlertsEnabled && shouldConfirmWalkIn(device)) {
        setConfirmDevice(device);
        return;
      }
      setOpenDevice(device);
    }
  }

  /**
   * A pick from a tile's Actions menu. Deliberately NOT behind the
   * out-of-service guard above: editing, deleting and "back in service" are
   * exactly the actions an out-of-service machine exists for.
   */
  function handleMenuAction(device: DeviceDto, item: DeviceMenuItemId): void {
    switch (item) {
      case 'edit':
        setEditingDevice(device);
        return;
      case 'delete':
        setDeletingDevice(device);
        return;
      case 'outstandingBill':
        setBillsDevice(device);
        return;
      case 'endReservation':
        setEndingDevice(device);
        return;
      // One dialog for both halves: it reads `session.paused` itself, so a
      // poll that landed while the menu was open cannot send a pause at an
      // already-paused sitting.
      case 'holdClock':
      case 'continueClock':
        setHoldingDevice(device);
        return;
      case 'voidSession':
        setVoidingDevice(device);
        return;
      case 'currentCost':
        // A display row, not an action.
        return;
      case 'pair':
        setPairingDevice(device);
        return;
      case 'serviceState':
        setServiceDevice(device);
        return;
    }
  }

  /**
   * Done on a tile's launcher notice (owner, 2026-10-05): the venue account
   * is signed back in. The notice leaves on the re-read, not optimistically —
   * the server's audit trail is what says it was answered, and a refused
   * press must leave it on the tile with the reason above the board.
   */
  async function handleLauncherNoticeDone(device: DeviceDto, auditId: string): Promise<void> {
    setNoticeError(null);
    try {
      await markLauncherNoticeDone(device.id, auditId);
      await reload({ quiet: true });
    } catch {
      setNoticeError(t('deviceBoard.launcherNotice.doneFailed', { device: device.name }));
    }
  }

  function handleBulkAction(action: BulkActionId): void {
    // Reserve is not a bulk RUN, it is a bulk FORM: the same panel the tile's
    // Reserve button opens, filled in once and applied to every ticked machine.
    // A party of five is one name, one tab, one bill.
    if (action === 'reserve') {
      setBulkReserve(selectedDevices);
      return;
    }
    setPendingBulk(action);
  }

  function runBulk(action: PendingBulk): Promise<BulkOutcome[]> {
    switch (action) {
      case 'reserve':
        // Unreachable: handled above as a form, never as a confirm-and-run.
        return Promise.resolve([]);
      case 'play':
        return bulkPlay(selectedDevices);
      case 'shutdown':
        return bulkPower(selectedDevices, 'shutdown');
      case 'restart':
        return bulkPower(selectedDevices, 'restart');
      case 'supportUnlock':
        return bulkSupportUnlock(selectedDevices);
      case 'pause':
        // Unreachable: BulkPauseDialog runs it, with the rounds it asked.
        return Promise.resolve([]);
    }
  }
}

/** One filter chip (card `.chip`): live count in the label, cyan when armed. */
function FilterChip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={[
        'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors max-md:min-h-10',
        on
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-subtle text-content-secondary hover:bg-hover',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function ZoomButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="grid h-6 w-6 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-content max-md:h-10 max-md:w-10"
    >
      {children}
    </button>
  );
}
